<template>
  <div class="fts-apps">
    <div class="apps-header">
      <div class="apps-header__l">
        <div class="apps-title">
          Apps
          <fts-badge color="warning" variant="pill" class="apps-alpha-badge">alpha</fts-badge>
        </div>
        <div class="apps-subtitle">
          Preconfigured modules GNey can act through — install one, add its credentials, and the
          workspace gains the capability. Apps have no entries and their design is locked.
        </div>
      </div>

      <div class="apps-header__r">
        <fts-tippy content="Refresh">
          <fts-btn
            slot="trigger"
            icon
            text
            :disabled="loading"
            @click="load"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </fts-btn>
        </fts-tippy>
      </div>
    </div>

    <div class="apps-body">
      <!-- admin gate: catalog stays visible read-only for non-admin internal users -->
      <div class="apps-plan-banner" v-if="notPermitted && !loading && !notEnabled">
        <font-awesome-icon :icon="['fas', 'lock']" class="apps-plan-banner-icon" />
        <div class="apps-plan-banner-text">
          Only workspace admins can install and configure apps. You can see what is available —
          ask an admin to set one up.
        </div>
      </div>

      <div class="apps-loading" v-if="loading">
        <fts-spinner />
      </div>

      <div class="apps-empty" v-else-if="notEnabled">
        <font-awesome-icon :icon="['fas', 'bolt']" size="2x" class="apps-empty-icon" />
        <div class="apps-empty-title">Apps are not enabled</div>
        <div class="apps-empty-desc">
          This environment has apps switched off. Contact your administrator.
        </div>
      </div>

      <div class="apps-grid" v-else>
        <div
          class="app-card"
          :class="{ 'app-card--unavailable': !x.available }"
          v-for="x in apps"
          :key="x.id"
        >
          <div class="app-card-head">
            <span class="app-card-emoji">{{ x.emoji }}</span>
            <span class="app-card-name">{{ x.name }}</span>
            <span class="app-card-badge">
              <fts-badge v-if="!x.available" color="text" variant="pill">
                {{ x.id === 'periskope' ? 'API pending' : 'Coming soon' }}
              </fts-badge>
              <fts-badge v-else-if="x.installed && isConnected(x)" color="success" variant="pill">
                Connected
              </fts-badge>
              <fts-badge v-else-if="x.installed" color="warning" variant="pill">
                Not connected
              </fts-badge>
            </span>
          </div>

          <div class="app-card-desc">{{ x.description }}</div>

          <div class="app-card-actions" v-if="x.available && !x.installed">
            <fts-btn
              color="primary"
              text-transform-init
              :disabled="notPermitted || installing === x.id"
              @click="install(x)"
            >
              {{ installing === x.id ? 'Installing…' : 'Install' }}
            </fts-btn>
          </div>

          <!-- Notifications: nothing to configure — it only reaches workspace members, through
               the platform's own channels (docs/teammate-module-store-notifications-proposal.md §8). -->
          <div class="app-card-config app-card-config--zero" v-if="x.installed && x.id === 'notifications'">
            <div class="app-config-zero">
              <div class="app-config-zero-row">
                <span class="app-config-zero-name">Notify</span>
                <span class="app-config-zero-desc">In-app: the bell, a mobile push and the notification reader, with entry chips.</span>
              </div>
              <div class="app-config-zero-row">
                <span class="app-config-zero-name">Notify by Email</span>
                <span class="app-config-zero-desc">The same message to the member's account email address.</span>
              </div>
              <div class="app-config-hint">
                Recipients are always workspace members — GNey and automations address them by name, never by
                address, so there are no lists to keep. Sends to more than a few people ask for your confirmation.
              </div>
            </div>
            <div class="app-config-actions">
              <fts-btn
                color="primary"
                text-transform-init
                :disabled="notPermitted || testing"
                @click="test(x)"
              >
                {{ testing ? 'Sending…' : testLabel(x) }}
              </fts-btn>
            </div>
            <div class="app-config-result" v-if="testResult && testResultApp === x.id">
              <font-awesome-icon
                :icon="['fas', testResult.ok ? 'check' : 'times']"
                :class="testResult.ok ? 'app-config-result--ok' : 'app-config-result--fail'"
              />
              <span v-if="testResult.ok">{{ testOkText(x, testResult) }}</span>
              <span v-else>{{ testErrorText(x, testResult.error, testResult.message) }}</span>
            </div>
          </div>

          <!-- Per-app config. Every credential field is WRITE-ONLY: never loaded back,
               saving replaces the stored one, blank leaves it as it is. -->
          <div class="app-card-config" v-else-if="x.installed && isConfigurable(x)">
            <div class="app-config-secure">
              <div class="app-config-secure-title">
                <font-awesome-icon :icon="['fas', 'lock']" class="app-config-lock" />
                Secure — write-only
              </div>

              <template v-if="x.id === 'slack'">
                <div class="app-config-field">
                  <label class="app-config-label">Bot token</label>
                  <input
                    class="app-config-input"
                    type="password"
                    v-model="form(x).token"
                    placeholder="xoxb-…"
                    autocomplete="new-password"
                  />
                </div>
              </template>

              <template v-else-if="x.id === 'email'">
                <div class="app-config-field">
                  <label class="app-config-label">SMTP username</label>
                  <input class="app-config-input" type="text" v-model="form(x).smtpUser" placeholder="notifications@yourdomain.com" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">SMTP password</label>
                  <input class="app-config-input" type="password" v-model="form(x).smtpPassword" autocomplete="new-password" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">SMTP server</label>
                  <input class="app-config-input" type="text" v-model="form(x).smtpServer" placeholder="smtp.yourdomain.com" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">Port</label>
                  <input class="app-config-input app-config-input--narrow" type="number" v-model.number="form(x).smtpPort" placeholder="587" />
                  <label class="app-config-checkbox">
                    <input type="checkbox" v-model="form(x).smtpSsl" /> Use SSL
                  </label>
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">Send from</label>
                  <input class="app-config-input" type="text" v-model="form(x).senderAddress" placeholder="notifications@yourdomain.com" />
                </div>
              </template>

              <template v-else-if="x.id === 'outlook'">
                <div class="app-config-field">
                  <label class="app-config-label">Tenant ID</label>
                  <input class="app-config-input" type="text" v-model="form(x).tenantId" placeholder="00000000-0000-0000-0000-000000000000" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">Client ID</label>
                  <input class="app-config-input" type="text" v-model="form(x).clientId" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">Client secret</label>
                  <input class="app-config-input" type="password" v-model="form(x).clientSecret" autocomplete="new-password" />
                </div>
              </template>

              <template v-else-if="x.id === 'periskope'">
                <div class="app-config-field">
                  <label class="app-config-label">API key</label>
                  <input class="app-config-input" type="password" v-model="form(x).apiKey" autocomplete="new-password" />
                </div>
                <div class="app-config-field">
                  <label class="app-config-label">WhatsApp number</label>
                  <input class="app-config-input" type="text" v-model="form(x).orgPhone" placeholder="60123456789" />
                </div>
              </template>

              <!-- WasenderApi asks for one value, not two: its key is scoped to a session, so the
                   key already names which WhatsApp number sends. Nothing here to keep in step. -->
              <template v-else-if="x.id === 'wasender'">
                <div class="app-config-field">
                  <label class="app-config-label">API key</label>
                  <input class="app-config-input" type="password" v-model="form(x).apiKey" autocomplete="new-password" />
                </div>
              </template>

              <div class="app-config-hint">{{ secureHint(x) }}</div>
            </div>

            <div class="app-config-field" v-if="x.id === 'slack'">
              <label class="app-config-label">Default channel</label>
              <input class="app-config-input" type="text" v-model="form(x).defaultChannel" placeholder="#general" />
            </div>

            <template v-if="hasRecipientLists(x)">
              <div class="app-config-field" v-if="x.id === 'outlook'">
                <label class="app-config-label">Send from</label>
                <input class="app-config-input" type="text" v-model="form(x).sender" placeholder="gney@yourdomain.com" />
              </div>
              <div class="app-config-field" v-if="x.id === 'email'">
                <label class="app-config-label">Sender name</label>
                <input class="app-config-input" type="text" v-model="form(x).senderName" placeholder="Acme Support" />
              </div>
              <div class="app-config-field" v-if="isMailApp(x)">
                <label class="app-config-label">Always CC</label>
                <input class="app-config-input" type="text" v-model="form(x).defaultCc" placeholder="records@yourdomain.com" />
              </div>

              <!-- Recipient tiers: the allowlist. Anyone who is neither a workspace member
                   nor on a list here is refused, not merely confirmed. -->
              <div class="app-config-lists">
                <div class="app-config-lists-head">
                  <span class="app-config-lists-title">{{ listsTitle(x) }}</span>
                  <fts-btn text text-transform-init :disabled="notPermitted" @click="addList(x)">
                    <font-awesome-icon :icon="['fas', 'plus']" /> Add list
                  </fts-btn>
                </div>
                <div class="app-config-hint">{{ listsHint(x) }}</div>

                <div class="app-config-list" v-for="(list, i) in form(x).recipientLists" :key="i">
                  <div class="app-config-list-head">
                    <input class="app-config-input" type="text" v-model="list.name" placeholder="List name" />
                    <select class="app-config-select" v-model="list.tier">
                      <option value="hybrid">Confirm each send</option>
                      <option value="ai">Send automatically</option>
                    </select>
                    <fts-btn icon text :disabled="notPermitted" @click="removeList(x, i)">
                      <font-awesome-icon :icon="['fas', 'times']" />
                    </fts-btn>
                  </div>
                  <textarea
                    class="app-config-textarea"
                    v-model="list.addressText"
                    rows="2"
                    :placeholder="listsPlaceholder(x)"
                  ></textarea>
                </div>
              </div>
            </template>

            <div class="app-config-actions">
              <!-- Revokes the stored credentials only; the module and its history stay. -->
              <fts-btn
                text
                text-transform-init
                v-if="isConnected(x)"
                :disabled="notPermitted || disconnecting"
                @click="disconnect(x)"
              >
                {{ disconnecting ? 'Disconnecting…' : 'Disconnect' }}
              </fts-btn>
              <fts-btn
                text
                text-transform-init
                :disabled="notPermitted || testing"
                @click="test(x)"
              >
                {{ testing ? 'Testing…' : testLabel(x) }}
              </fts-btn>
              <fts-btn
                color="primary"
                text-transform-init
                :disabled="notPermitted || saving"
                @click="save(x)"
              >
                {{ saving ? 'Saving…' : 'Save' }}
              </fts-btn>
            </div>

            <div class="app-config-result" v-if="saveResult && saveResultApp === x.id">
              <font-awesome-icon
                :icon="['fas', saveResult.ok ? 'check' : 'times']"
                :class="saveResult.ok ? 'app-config-result--ok' : 'app-config-result--fail'"
              />
              <span>{{ saveResult.message }}</span>
            </div>

            <div class="app-config-result" v-if="testResult && testResultApp === x.id">
              <font-awesome-icon
                :icon="['fas', testResult.ok ? 'check' : 'times']"
                :class="testResult.ok ? 'app-config-result--ok' : 'app-config-result--fail'"
              />
              <span v-if="testResult.ok">{{ testOkText(x, testResult) }}</span>
              <span v-else>{{ testErrorText(x, testResult.error, testResult.state || testResult.message) }}</span>
            </div>

            <!-- End-to-end check: a connection test proves the credentials authenticate, not that
                 a message arrives. The admin picks the destination; the server owns the wording. -->
            <div class="app-config-send">
              <div class="app-config-send-title">Send a test message</div>
              <div class="app-config-field">
                <label class="app-config-label">{{ sendTestLabel(x) }}</label>
                <input
                  class="app-config-input"
                  type="text"
                  v-model="form(x).testTo"
                  :placeholder="sendTestPlaceholder(x)"
                  @keyup.enter="sendTestMessage(x)"
                />
                <fts-btn
                  text
                  text-transform-init
                  :disabled="notPermitted || sendingTest"
                  @click="sendTestMessage(x)"
                >
                  {{ sendingTest && sendResultApp === x.id ? 'Sending…' : 'Send' }}
                </fts-btn>
              </div>
              <div class="app-config-hint">{{ sendTestHint(x) }}</div>

              <div class="app-config-result" v-if="sendResult && sendResultApp === x.id">
                <font-awesome-icon
                  :icon="['fas', sendResult.ok ? 'check' : 'times']"
                  :class="sendResult.ok ? 'app-config-result--ok' : 'app-config-result--fail'"
                />
                <span v-if="sendResult.ok">{{ sendOkText(x, sendResult) }}</span>
                <span v-else>{{ sendErrorText(x, sendResult) }}</span>
              </div>
            </div>

            <div class="app-config-meta">
              <span><font-awesome-icon :icon="['fas', 'lock']" /> Design locked</span>
              <span>No entries or history</span>
              <span>5 sends per turn</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { clientStore } from '../store/clientStore';

import FTSBadge from '../elements/FTSBadge';
import FTSBtn from '../elements/FTSBtn';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';

export default {
  name: 'FTSApps',
  components: {
    'fts-badge': FTSBadge,
    'fts-btn': FTSBtn,
    'fts-spinner': FTSSpinner,
    'fts-tippy': FTSTippy,
  },
  data() {
    return {
      loading: false,
      notEnabled: false,
      // Admin gate: the catalog stays browsable; install/config are disabled.
      notPermitted: false,
      apps: [],
      status: {},
      // One form per app id; credential fields always start blank (write-only).
      forms: {},
      installing: null,
      saving: false,
      testing: false,
      testResult: null,
      testResultApp: null,
      // Real send to a destination the admin types — separate from the connection test above,
      // so a credential failure and an undeliverable destination stay distinguishable.
      sendingTest: false,
      sendResult: null,
      sendResultApp: null,
      // Save outcome: { ok, message } — write-only fields clear on success, so without
      // this line a failed save (e.g. a swallowed 403) is indistinguishable from a good one.
      saveResult: null,
      saveResultApp: null,
      disconnecting: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.notEnabled = false;
      this.notPermitted = false;
      this.testResult = null;
      this.sendResult = null;
      try {
        try {
          const availability = await clientStore.dispatch('getAppsAvailability');
          const data = availability && availability.data;
          if (data && data.enabled === false) {
            this.notEnabled = true;
            this.apps = [];
            return;
          }
          this.notPermitted = !!(data && data.enabled && !data.permitted);
        } catch (availabilityErr) {
          // Older backend without the probe: fall through to the catalog call.
        }

        const res = await clientStore.dispatch('getApps');
        this.apps = res.data || [];

        await Promise.all(
          this.apps.filter((a) => a.installed).map((a) => this.loadStatus(a.id)),
        );
      } catch (err) {
        if (err && err.response && err.response.status === 404) {
          this.notEnabled = true;
        }
        this.apps = [];
      } finally {
        this.loading = false;
      }
    },
    async loadStatus(appId) {
      try {
        const res = await clientStore.dispatch('getAppStatus', appId);
        const data = res.data || {};
        this.$set(this.status, appId, data);
        // Only the non-secret settings round-trip; credentials are never read back.
        const f = this.form({ id: appId });
        f.defaultChannel = data.defaultChannel || f.defaultChannel;
        f.senderName = data.senderName || f.senderName;
        f.sender = data.sender || f.sender;
        f.defaultCc = data.defaultCc || f.defaultCc;
        f.smtpUser = data.smtpUser || f.smtpUser;
        f.smtpServer = data.smtpServer || f.smtpServer;
        f.smtpPort = data.smtpPort || f.smtpPort;
        f.smtpSsl = data.smtpSsl != null ? data.smtpSsl : f.smtpSsl;
        f.senderAddress = data.senderAddress || f.senderAddress;
        f.tenantId = data.tenantId || f.tenantId;
        f.clientId = data.clientId || f.clientId;
        f.orgPhone = data.orgPhone || f.orgPhone;
        if (data.recipientLists) {
          // Addresses edit as free text; they are split again on save.
          f.recipientLists = data.recipientLists.map((l) => ({
            name: l.name,
            tier: l.tier,
            addressText: (l.addresses || []).join(', '),
          }));
        }
      } catch (err) {
        this.$set(this.status, appId, {});
      }
    },
    /** Lazily-created edit state for one app. Credentials always start empty. */
    form(app) {
      if (!this.forms[app.id]) {
        this.$set(this.forms, app.id, {
          token: '',
          defaultChannel: '',
          smtpUser: '',
          smtpPassword: '',
          smtpServer: '',
          smtpPort: null,
          smtpSsl: true,
          senderAddress: '',
          senderName: '',
          tenantId: '',
          clientId: '',
          clientSecret: '',
          apiKey: '',
          orgPhone: '',
          sender: '',
          defaultCc: '',
          recipientLists: [],
          testTo: '',
        });
      }
      return this.forms[app.id];
    },
    isMailApp(app) {
      return app.id === 'email' || app.id === 'outlook';
    },
    // Two vendors, one channel: they take the same payload and differ only in the address form
    // and the wording, so everything structural branches on this rather than on a vendor id.
    isWhatsappApp(app) {
      return app.id === 'periskope' || app.id === 'wasender';
    },
    hasRecipientLists(app) {
      return this.isMailApp(app) || this.isWhatsappApp(app);
    },
    isConfigurable(app) {
      return app.id === 'slack' || this.hasRecipientLists(app);
    },
    listsTitle(app) {
      return this.isWhatsappApp(app) ? 'Who GNey may message' : 'Who GNey may email';
    },
    listsHint(app) {
      if (app.id === 'wasender') {
        return 'A workspace member whose profile carries a phone number can always be messaged. '
          + 'For anyone else — a customer, a group, a channel — add them to a list here. Numbers '
          + 'on no list are refused, not asked about.';
      }
      if (app.id === 'periskope') {
        return 'WhatsApp has no “workspace member” shortcut — every number or group GNey may '
          + 'message has to be on a list here. Anything else is refused, not asked about.';
      }
      return 'Everyone in this workspace can always be emailed. For anyone else, add them to a '
        + 'list — addresses on no list are refused, not asked about.';
    },
    listsPlaceholder(app) {
      if (app.id === 'wasender') return '+60123456789, 120363041234567890@g.us';
      return app.id === 'periskope'
        ? '60123456789, 120363041234567890@g.us'
        : 'buyer@customer.com, @partner.com';
    },
    isConnected(app) {
      const s = this.status[app.id];
      return !!(s && s.connected);
    },
    /**
     * Revokes the stored credentials. The module and its delivery history stay — this is not a
     * delete. Slack and Email share their credentials with the workspace's own notification
     * channels, so the server refuses those with needs_confirmation until the admin has been told
     * exactly what else stops working.
     */
    async disconnect(app) {
      this.saveResult = null;
      this.saveResultApp = app.id;
      this.disconnecting = true;
      try {
        let res = await clientStore.dispatch('disconnectApp', { appId: app.id });
        let data = res.data || {};

        if (data.error === 'needs_confirmation') {
          const proceed = window.confirm(
            `Disconnect ${app.name}? This also stops ${data.alsoStops}.`
            + '\n\nThe module and its history stay — only the saved credentials are removed.');
          if (!proceed) {
            this.saveResult = null;
            return;
          }
          res = await clientStore.dispatch('disconnectApp', { appId: app.id, confirmed: true });
          data = res.data || {};
        }

        if (data.ok) {
          this.saveResult = {
            ok: true,
            message: 'Disconnected — the saved credentials were removed.'
              + (data.alsoStopped ? ` This also stopped ${data.alsoStopped}.` : ''),
          };
          await this.loadStatus(app.id);
        } else {
          this.saveResult = { ok: false, message: 'Could not disconnect — please try again.' };
        }
      } catch (err) {
        this.saveResult = { ok: false, message: 'Could not disconnect — please try again.' };
      } finally {
        this.disconnecting = false;
      }
    },
    secureHint(app) {
      if (app.id === 'email') {
        return 'Optional — without SMTP the app sends through the Inistate mail server. '
          + 'Add your own to send as your domain. Leave the password blank to keep the stored one.';
      }
      if (app.id === 'outlook') {
        return 'From your Azure app registration. It needs the Mail.Send application permission '
          + 'with admin consent. Stored server-side, never shown again.';
      }
      if (app.id === 'periskope') {
        return 'From the Periskope console (Settings → Integrations → API). The number is the '
          + 'WhatsApp account messages are sent from. Leave the key blank to keep the stored one.';
      }
      if (app.id === 'wasender') {
        return 'From your WasenderApi session — the key is tied to the WhatsApp number you linked '
          + 'by QR, so pasting a different session’s key changes who messages come from. Leave it '
          + 'blank to keep the stored one.';
      }
      return 'Stored server-side, never shown again. Leave blank to keep the current token.';
    },
    testLabel(app) {
      if (app.id === 'notifications') return 'Send me a test notification';
      return app.id === 'email' ? 'Send test email' : 'Test connection';
    },
    sendTestLabel(app) {
      if (app.id === 'slack') return 'Channel';
      if (this.isWhatsappApp(app)) return 'Number';
      return 'Address';
    },
    sendTestPlaceholder(app) {
      if (app.id === 'slack') return '#general';
      if (app.id === 'wasender') return '+60123456789';
      if (app.id === 'periskope') return '60123456789';
      return 'you@yourdomain.com';
    },
    sendTestHint(app) {
      const wording = 'The message wording is fixed — you choose where it goes.';
      if (app.id === 'slack') {
        return `Leave blank to use the default channel. Invite the bot to the channel first. ${wording}`;
      }
      if (app.id === 'wasender') {
        return `A full international number, a group id or an @username. ${wording}`;
      }
      if (app.id === 'periskope') {
        return `A phone number or Periskope chat id. ${wording}`;
      }
      return `Leave blank to send to your own address. ${wording}`;
    },
    addList(app) {
      this.form(app).recipientLists.push({ name: '', tier: 'hybrid', addressText: '' });
    },
    removeList(app, index) {
      this.form(app).recipientLists.splice(index, 1);
    },
    async install(app) {
      this.installing = app.id;
      try {
        await clientStore.dispatch('installApp', app.id);
        await this.load();
      } catch (err) {
        console.error('[Apps] install failed', err);
      } finally {
        this.installing = null;
      }
    },
    async save(app) {
      this.saving = true;
      this.testResult = null;
      this.sendResult = null;
      this.saveResult = null;
      this.saveResultApp = app.id;
      const f = this.form(app);
      try {
        const config = {
          token: f.token || null,
          defaultChannel: app.id === 'slack' ? f.defaultChannel : undefined,
        };
        if (this.isMailApp(app)) {
          config.defaultCc = f.defaultCc || '';
          config.senderName = f.senderName || '';
        }
        if (this.hasRecipientLists(app)) {
          config.recipientLists = f.recipientLists
            .map((l) => ({
              name: l.name || 'List',
              tier: l.tier,
              addresses: (l.addressText || '')
                .split(/[,;\n]/)
                .map((x) => x.trim())
                .filter((x) => x.length > 0),
            }));
        }
        if (app.id === 'email') {
          config.smtpUser = f.smtpUser || null;
          config.smtpPassword = f.smtpPassword || null;
          config.smtpServer = f.smtpServer || null;
          config.smtpPort = f.smtpPort || null;
          config.smtpSsl = f.smtpSsl;
          config.senderAddress = f.senderAddress || null;
        }
        if (app.id === 'outlook') {
          config.tenantId = f.tenantId || null;
          config.clientId = f.clientId || null;
          config.clientSecret = f.clientSecret || null;
          config.sender = f.sender || '';
        }
        if (app.id === 'periskope') {
          config.apiKey = f.apiKey || null;
          config.orgPhone = f.orgPhone || null;
        }
        if (app.id === 'wasender') {
          config.apiKey = f.apiKey || null;
        }

        await clientStore.dispatch('configureApp', { appId: app.id, config });
        // Clear the write-only fields after a successful save — they are never read back.
        f.token = '';
        f.smtpPassword = '';
        f.clientSecret = '';
        f.apiKey = '';
        this.saveResult = { ok: true, message: 'Saved.' };
        await this.loadStatus(app.id);
      } catch (err) {
        console.error('[Apps] save failed', err);
        this.saveResult = { ok: false, message: this.saveErrorText(err) };
      } finally {
        this.saving = false;
      }
    },
    saveErrorText(err) {
      const res = err && err.response;
      const data = res && res.data;
      if (res && res.status === 403 && data && data.error === 'admin_only') {
        return 'Not saved — only a workspace admin can change app settings.';
      }
      if (res && res.status === 404) {
        return 'Not saved — the app store is not enabled on this server.';
      }
      return 'Not saved — the request failed' + (res ? ' (HTTP ' + res.status + ')' : '') + '. Please try again.';
    },
    async test(app) {
      this.testing = true;
      this.testResult = null;
      this.sendResult = null;
      this.saveResult = null;
      this.testResultApp = app.id;
      try {
        const res = await clientStore.dispatch('testApp', app.id);
        this.testResult = res.data || { ok: false, error: 'network_error' };
      } catch (err) {
        // Swallowing the status here reported an admin gate (403) and a disabled store (404) as
        // 'network_error', pointing admins at their Slack token when the request never left the
        // server. Mirrors sendTestMessage below.
        const status = err && err.response && err.response.status;
        const data = (err && err.response && err.response.data) || {};
        this.testResult = {
          ok: false,
          error: status === 403 ? 'admin_only'
            : status === 404 ? 'store_disabled'
            : (data.error || 'network_error'),
          message: data.message,
        };
      } finally {
        this.testing = false;
      }
    },
    testOkText(app, result) {
      if (app.id === 'notifications') {
        return 'Sent — open the bell to see it' + (result.sentTo ? ' (to ' + result.sentTo + ')' : '') + '.';
      }
      if (app.id === 'email') {
        return 'Test message sent to ' + result.sentTo
          + (result.ownSender ? ' using your SMTP server.' : ' using the Inistate sender.');
      }
      if (app.id === 'outlook') {
        return 'Connected — sends will come from ' + result.sender + '.';
      }
      if (app.id === 'periskope') {
        return 'Connected to ' + (result.phoneName || 'Periskope')
          + ' (' + result.phone + ') — ' + (result.state || 'ready').toLowerCase() + '.';
      }
      if (app.id === 'wasender') {
        return 'Connected — the linked WhatsApp session is '
          + (result.state || 'connected').toLowerCase() + '.';
      }
      let text = 'Connected to ' + (result.team || 'Slack')
        + (result.botUser ? ' as ' + result.botUser : '');
      // Passing but partial: without a read scope the token cannot look a channel NAME up, so
      // "#general" would fail while a pasted channel id works.
      if (result.warning === 'missing_read_scope') {
        text += '. Note: the token is missing ' + (result.scopes || 'channels:read')
          + ', so it can only send to a channel id (C…), not a #name. Add the scope to the Slack '
          + 'app’s Bot Token Scopes and reinstall to use names.';
      }
      return text;
    },
    testErrorText(app, error, message) {
      if (error === 'not_delivered') {
        return 'The notification could not be delivered' + (message ? ': ' + message : '.') ;
      }
      if (error === 'no_test_recipient') return 'Could not work out who you are — sign in again and retry.';
      if (error === 'not_connected') {
        return 'No credentials saved yet — fill them in and save first.';
      }
      // Refusals from our own API, not the service — nothing about the credentials is wrong.
      if (error === 'admin_only') {
        return 'Only a workspace admin can test app connections.';
      }
      if (error === 'store_disabled') {
        return 'The app store is not enabled on this server.';
      }
      // The request never reached the service: DNS, TLS, a firewall or a proxy. The credentials
      // are untested, so pointing at the token here would send admins down the wrong path.
      if (error === 'network_error') {
        return 'Could not reach the service' + (message ? ': ' + message : '')
          + '. The credentials were not tested — this is a connectivity problem on the server.';
      }
      if (error === 'invalid_auth') return 'Slack rejected the token — check it and save again.';
      // Send-path refusals: the token is fine, the destination is not.
      if (error === 'not_in_channel') {
        return 'The bot is not a member of that channel — invite it there, then send again.';
      }
      if (error === 'channel_not_found') {
        return 'Slack has no such channel visible to the bot — check the name.';
      }
      // The token authenticates but cannot post — sends would fail with missing_scope.
      if (error === 'missing_scope') {
        return 'Token is valid but missing the chat:write scope — add it to the Slack app’s '
          + 'Bot Token Scopes, reinstall the app, then save the new token here.';
      }
      if (error === 'invalid_client' || error === 'unauthorized_client') {
        return 'Microsoft rejected the credentials — check the client id and secret, and whether '
          + 'the secret has expired.';
      }
      if (error === 'missing_permission'
        || error === 'ErrorAccessDenied' || error === 'Authorization_RequestDenied') {
        return 'The app registration is missing the Mail.Send application permission (or a tenant '
          + 'admin has not granted consent for it) — sends would be refused.';
      }
      if (error === 'ErrorInvalidUser' || error === 'ErrorSendAsDenied') {
        return 'Microsoft would not send as that mailbox — check the “Send from” address and that '
          + 'the app is allowed to send as it.';
      }
      if (error === 'no_sender') return 'Set the mailbox to send from, then save.';
      if (error === 'invalid_key') {
        if (app.id === 'wasender') {
          return 'WasenderApi rejected the API key — copy the session’s key from your WasenderApi '
            + 'dashboard and save it here.';
        }
        return 'Periskope rejected the API key — generate a new one in the Periskope console '
          + 'and save it here.';
      }
      // The key is fine and the session is not: the linked device dropped off, so sends would be
      // accepted and never arrive. The remedy is the phone, not the key — say which state it is in.
      if (error === 'session_not_connected') {
        const state = (message || '').toLowerCase();
        if (state === 'need_scan' || state === 'logged_out' || state === 'expired') {
          return 'The API key works, but the WhatsApp session is “' + state + '” — re-link the '
            + 'number by scanning the QR in WasenderApi. Until then nothing sends.';
        }
        return 'The API key works, but the WhatsApp session is not connected'
          + (message ? ' (' + message + ')' : '') + ' — re-link it in WasenderApi, otherwise '
          + 'messages are accepted and never arrive.';
      }
      if (error === 'no_status') {
        return 'WasenderApi answered without a session status — try again, and check the session '
          + 'in their dashboard if it keeps happening.';
      }
      if (error === 'phone_not_on_account') {
        return 'That number is not one of the phones on this Periskope account — check it and save again.';
      }
      if (error === 'phone_not_connected') {
        return 'The WhatsApp phone is not connected in Periskope — reconnect it there, otherwise '
          + 'messages queue and never arrive.';
      }
      if (error === 'no_test_recipient') {
        return 'No address to test with — set the “Send from” address and save first.';
      }
      if (error === 'send_failed') {
        return 'The send failed' + (message ? ': ' + message : '') + '.';
      }
      return 'Connection test failed' + (error ? ' (' + error + ')' : '') + '.';
    },
    async sendTestMessage(app) {
      this.sendingTest = true;
      this.sendResult = null;
      this.sendResultApp = app.id;
      this.testResult = null;
      this.saveResult = null;
      try {
        const res = await clientStore.dispatch('sendAppTestMessage', {
          appId: app.id,
          to: (this.form(app).testTo || '').trim(),
        });
        this.sendResult = res.data || { ok: false, error: 'network_error' };
      } catch (err) {
        const status = err && err.response && err.response.status;
        const data = (err && err.response && err.response.data) || {};
        this.sendResult = {
          ok: false,
          error: status === 403 ? 'admin_only' : (data.error || 'network_error'),
        };
      } finally {
        this.sendingTest = false;
      }
    },
    sendOkText(app, result) {
      const where = result.sentTo;
      if (app.id === 'periskope') {
        return 'Handed to Periskope for ' + where + ' — WhatsApp delivers asynchronously, so '
          + 'check the phone.';
      }
      if (app.id === 'wasender') {
        return 'Queued with WasenderApi for ' + where + ' — WhatsApp delivers asynchronously, so '
          + 'check the phone.';
      }
      if (app.id === 'email') {
        return 'Test message sent to ' + where
          + (result.ownSender ? ' using your SMTP server.' : ' using the Inistate sender.');
      }
      return 'Test message sent to ' + where + '.';
    },
    sendErrorText(app, result) {
      const error = result.error;
      if (error === 'admin_only') {
        return 'Not sent — only a workspace admin can send test messages.';
      }
      if (error === 'no_recipient') {
        return app.id === 'slack'
          ? 'No channel given, and no default channel is configured.'
          : 'No destination given, and there is no default to fall back to.';
      }
      if (error === 'invalid_recipient') {
        return app.id === 'wasender'
          ? 'That is not a phone number, a group id or an @username.'
          : 'That is not a phone number or a Periskope chat id.';
      }
      // A 2xx that queued nothing. Falling through would call this a failed CONNECTION test,
      // which is the opposite of what happened — the credentials were fine, the send was not.
      if (error && error.indexOf('send_refused') === 0) {
        return 'WasenderApi took the request but queued nothing — nothing was sent'
          + (error.length > 'send_refused: '.length
            ? ' (' + error.slice('send_refused: '.length) + ')' : '') + '.';
      }
      // Everything else is the channel's own refusal — the same codes a real GNey send hits,
      // so the existing wording is the right wording.
      return this.testErrorText(app, error, result.message);
    },
  },
};
</script>

<style lang="scss">
.fts-apps {
  padding: 24px 28px;
  max-width: 960px;

  .apps-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .apps-title {
    font-size: 20px;
    font-weight: 600;
    color: var(--text-color-title, #2c3e50);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .apps-alpha-badge {
    text-transform: lowercase;
  }

  .apps-subtitle {
    margin-top: 4px;
    font-size: 13px;
    color: var(--text-color-secondary, #7a8699);
    max-width: 560px;
    line-height: 1.5;
  }

  .apps-plan-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    border: 1px solid var(--border-color, #e3e7ee);
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 16px;
    font-size: 13px;
    color: var(--text-color-secondary, #7a8699);

    .apps-plan-banner-icon {
      margin-top: 2px;
    }
  }

  .apps-loading,
  .apps-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 0;
    color: var(--text-color-secondary, #7a8699);
  }

  .apps-empty-icon {
    margin-bottom: 12px;
    opacity: 0.5;
  }

  .apps-empty-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 4px;
    color: var(--text-color-title, #2c3e50);
  }

  .apps-empty-desc {
    font-size: 13px;
  }

  .apps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;
    align-items: start;
  }

  .app-card {
    border: 1px solid var(--border-color, #e3e7ee);
    border-radius: 8px;
    padding: 14px 16px;
    background: var(--background-primary, #fff);

    &.app-card--unavailable {
      opacity: 0.65;
    }
  }

  .app-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .app-card-emoji {
    font-size: 18px;
  }

  .app-card-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color-title, #2c3e50);
  }

  .app-card-badge {
    margin-left: auto;
  }

  .app-card-desc {
    margin-top: 6px;
    font-size: 12.5px;
    line-height: 1.5;
    color: var(--text-color-secondary, #7a8699);
  }

  .app-card-actions {
    margin-top: 12px;
  }

  .app-card-config {
    margin-top: 14px;
    border-top: 1px solid var(--border-color, #e3e7ee);
    padding-top: 12px;
  }

  .app-config-secure {
    border: 1px solid var(--border-color, #d8dee8);
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 10px;
  }

  .app-config-secure-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-title, #2c3e50);
    margin-bottom: 8px;

    .app-config-lock {
      margin-right: 4px;
    }
  }

  .app-config-field {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
  }

  .app-config-label {
    flex: 0 0 110px;
    font-size: 12.5px;
    color: var(--text-color-secondary, #7a8699);
    margin: 0;
  }

  .app-config-input {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--border-color, #d8dee8);
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 13px;
    background: var(--background-primary, #fff);
    color: inherit;

    &:focus {
      outline: none;
      border-color: var(--primary-color, #7c5cd1);
    }
  }

  .app-config-input--narrow {
    flex: 0 0 90px;
  }

  .app-config-checkbox {
    font-size: 12.5px;
    color: var(--text-color-secondary, #7a8699);
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    white-space: nowrap;
  }

  .app-config-select {
    flex: 0 0 160px;
    border: 1px solid var(--border-color, #d8dee8);
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 12.5px;
    background: var(--background-primary, #fff);
    color: inherit;
  }

  .app-config-textarea {
    width: 100%;
    border: 1px solid var(--border-color, #d8dee8);
    border-radius: 4px;
    padding: 6px 10px;
    font-size: 12.5px;
    line-height: 1.5;
    resize: vertical;
    background: var(--background-primary, #fff);
    color: inherit;

    &:focus {
      outline: none;
      border-color: var(--primary-color, #7c5cd1);
    }
  }

  .app-config-lists {
    margin-top: 12px;
    border-top: 1px solid var(--border-color, #e3e7ee);
    padding-top: 10px;
  }

  .app-config-lists-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .app-config-lists-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-title, #2c3e50);
  }

  .app-config-list {
    border: 1px solid var(--border-color, #e3e7ee);
    border-radius: 6px;
    padding: 8px 10px;
    margin-top: 8px;

    .app-config-list-head {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 6px;
    }
  }

  .app-config-zero { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 0.75rem; }
.app-config-zero-row { display: flex; gap: 0.5rem; align-items: baseline; font-size: 0.8125rem; line-height: 1.35; }
.app-config-zero-name { flex: 0 0 auto; padding: 0 0.5rem; border-radius: 999px; background: rgba(var(--fts-gray-100-rgb, 243, 244, 246), 0.9); font-weight: 600; }
.app-config-zero-desc { color: var(--fts-default, #6b7280); }
.app-config-hint {
    font-size: 11.5px;
    color: var(--text-color-secondary, #9aa4b5);
  }

  .app-config-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
  }

  .app-config-result {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    font-size: 12.5px;
    color: var(--text-color-secondary, #7a8699);

    .app-config-result--ok {
      color: #2f9e6e;
    }

    .app-config-result--fail {
      color: #d85a30;
    }
  }

  .app-config-send {
    margin-top: 12px;
    border-top: 1px solid var(--border-color, #e3e7ee);
    padding-top: 10px;

    .app-config-field {
      margin-top: 6px;
    }
  }

  .app-config-send-title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-color-title, #2c3e50);
  }

  .app-config-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    font-size: 11.5px;
    color: var(--text-color-secondary, #9aa4b5);
  }
}
</style>
